import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { FaCar, FaEdit, FaEye, FaTrash } from "react-icons/fa";
import { getToken } from "../../api/auth";
import {
  getVehicles,
  addVehicle,
  updateVehicle,
} from "../../api/routeOptimization";
import "./Vehicles.css";

const Vehicles = () => {
  const token = getToken();
  const [vehicles, setVehicles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    description: "",
    agentcode: "",
    city: "",
    postcode: "",
    startlocationlatitude: "",
    startlocationlongitude: "",
    endlocationlatitude: "",
    endlocationlongitude: "",
    starttime: "08:00:00",
    endtime: "16:00:00",
    vehicleplate: "",
    height: "",
    length: "",
    width: "",
    vehiclecubage: "",
    weight: "",
    active: true,
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getVehicles();
        if (response.success) {
          setVehicles(response.vehicles || []);
        } else {
          setError("Failed to fetch vehicles. Please try again.");
        }
      } catch (err) {
        setError("Failed to fetch vehicles. Please try again.");
      }
    };
    if (token) {
      fetchData();
    }
  }, [token]);

  useEffect(() => {
    // Auto-calculate vehiclecubage when height, length, or width changes
    const { height, length, width } = formData;
    if (height && length && width) {
      const cubage = Number(height) * Number(length) * Number(width);
      setFormData((prev) => ({ ...prev, vehiclecubage: cubage }));
    }
  }, [formData.height, formData.length, formData.width]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const filteredVehicles = vehicles.filter(
    (vehicle) =>
      vehicle.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.vehicleplate.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.city.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const openModal = () => {
    setFormData({
      id: null,
      description: "",
      agentcode: "",
      city: "",
      postcode: "",
      startlocationlatitude: "",
      startlocationlongitude: "",
      endlocationlatitude: "",
      endlocationlongitude: "",
      starttime: "08:00:00",
      endtime: "16:00:00",
      vehicleplate: "",
      height: "",
      length: "",
      width: "",
      vehiclecubage: "",
      weight: "",
      active: true,
    });
    setIsModalOpen(true);
    setError(null);
  };

  const openDetailsModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsDetailsModalOpen(true);
  };

  const openEditModal = (vehicle) => {
    setFormData({
      id: vehicle.id,
      description: vehicle.description,
      agentcode: vehicle.agentcode,
      city: vehicle.city,
      postcode: vehicle.postcode,
      startlocationlatitude: vehicle.startlocationlatitude,
      startlocationlongitude: vehicle.startlocationlongitude,
      endlocationlatitude: vehicle.endlocationlatitude,
      endlocationlongitude: vehicle.endlocationlongitude,
      starttime: vehicle.starttime,
      endtime: vehicle.endtime,
      vehicleplate: vehicle.vehicleplate,
      height: vehicle.height,
      length: vehicle.length,
      width: vehicle.width,
      vehiclecubage: vehicle.vehiclecubage,
      weight: vehicle.weight,
      active: vehicle.active,
    });
    setIsModalOpen(true);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const vehicleData = {
        id: formData.id || 0,
        description: formData.description,
        agentCode: formData.agentcode,
        city: formData.city,
        postCode: formData.postcode,
        startLocationLatitude: Number(formData.startlocationlatitude),
        startLocationLongitude: Number(formData.startlocationlongitude),
        endLocationLatitude: Number(formData.endlocationlatitude),
        endLocationLongitude: Number(formData.endlocationlongitude),
        startTime: formData.starttime,
        endTime: formData.endtime,
        vehiclePlate: formData.vehicleplate,
        height: Number(formData.height),
        length: Number(formData.length),
        width: Number(formData.width),
        vehicleCubage: Number(formData.vehiclecubage),
        weight: Number(formData.weight),
        active: Boolean(formData.active),
      };

      const response = formData.id
        ? await updateVehicle(vehicleData)
        : await addVehicle(vehicleData);

      // Check if response contains vehicle data (successful operation)
      if (response && response.id) {
        // Fetch updated vehicles list
        const updatedResponse = await getVehicles();
        if (updatedResponse.success) {
          setVehicles(updatedResponse.vehicles || []);
        }
        setError(`Vehicle ${formData.id ? "updated" : "added"} successfully!`);
        setTimeout(() => setError(null), 3000);
        setIsModalOpen(false);
        setFormData({
          id: null,
          description: "",
          agentcode: "",
          city: "",
          postcode: "",
          startlocationlatitude: "",
          startlocationlongitude: "",
          endlocationlatitude: "",
          endlocationlongitude: "",
          starttime: "08:00:00",
          endtime: "16:00:00",
          vehicleplate: "",
          height: "",
          length: "",
          width: "",
          vehiclecubage: "",
          weight: "",
          active: true,
        });
      } else {
        setError(`Failed to ${formData.id ? "update" : "add"} vehicle.`);
      }
    } catch (err) {
      setError(
        `Failed to ${formData.id ? "update" : "add"} vehicle. Please try again.`,
      );
    }
  };

  return (
    <div className="vehicles-container">
      <div className="floating-bg floating-bg-1"></div>
      <div className="floating-bg floating-bg-2"></div>
      <div className="floating-bg floating-bg-3"></div>
      <div className="page-header">
        <h2 className="vehicles-title">Vehicles</h2>
        <p className="page-subtitle">Manage your fleet of vehicles</p>
      </div>
      {error && <div className="success-message">{error}</div>}
      <div className="filters">
        <input
          type="text"
          placeholder="Search by description, plate, or city..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="glass-btn add-vehicle-btn" onClick={openModal}>
          <FaCar size={20} />
          Add Vehicle
        </button>
      </div>
      <div className="vehicles-grid">
        {filteredVehicles.map((vehicle) => (
          <div key={vehicle.id} className="vehicle-card">
            <div className="vehicle-card-header">
              <div className="vehicle-icon">
                <FaCar size={32} />
              </div>
              <div className="vehicle-basic-info">
                <h3 className="vehicle-name">{vehicle.description}</h3>
                <p className="vehicle-type">{vehicle.vehicleplate}</p>
              </div>
            </div>
            <div className="vehicle-card-body">
              <div className="vehicle-detail-row">
                <span className="detail-label">ID:</span>
                <span className="detail-value">{vehicle.id}</span>
              </div>
              <div className="vehicle-detail-row">
                <span className="detail-label">City:</span>
                <span className="detail-value">{vehicle.city}</span>
              </div>
              <div className="vehicle-detail-row">
                <span className="detail-label">Postcode:</span>
                <span className="detail-value">{vehicle.postcode}</span>
              </div>
              <div className="vehicle-detail-row">
                <span className="detail-label">Start Location:</span>
                <span className="detail-value">
                  {vehicle.startlocationlatitude},{" "}
                  {vehicle.startlocationlongitude}
                </span>
              </div>
              <div className="vehicle-detail-row">
                <span className="detail-label">End Location:</span>
                <span className="detail-value">
                  {vehicle.endlocationlatitude}, {vehicle.endlocationlongitude}
                </span>
              </div>
              <div className="vehicle-detail-row">
                <span className="detail-label">Operating Hours:</span>
                <span className="detail-value">
                  {vehicle.starttime} - {vehicle.endtime}
                </span>
              </div>
              <div className="vehicle-detail-row">
                <span className="detail-label">Dimensions:</span>
                <span className="detail-value">
                  {vehicle.height}×{vehicle.length}×{vehicle.width} cm
                </span>
              </div>
              <div className="vehicle-detail-row">
                <span className="detail-label">Cubage:</span>
                <span className="detail-value">
                  {vehicle.vehiclecubage} cm³
                </span>
              </div>
              <div className="vehicle-detail-row">
                <span className="detail-label">Weight:</span>
                <span className="detail-value">{vehicle.weight} kg</span>
              </div>
              <div className="vehicle-detail-row">
                <span className="detail-label">Status:</span>
                <span className="detail-value">
                  {vehicle.active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
            <div className="vehicle-card-actions">
              <button
                className="glass-btn btn-secondary"
                onClick={() => openEditModal(vehicle)}
              >
                <FaEdit size={16} /> Edit
              </button>
              <button
                className="glass-btn btn-primary"
                onClick={() => openDetailsModal(vehicle)}
              >
                <FaEye size={16} /> View Details
              </button>
              <button className="glass-btn btn-delete" disabled>
                <FaTrash size={16} /> Delete
              </button>
            </div>
          </div>
        ))}
        {filteredVehicles.length === 0 && (
          <div className="empty-state">
            <p>No vehicles found</p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{formData.id ? "Edit Vehicle" : "Add Vehicle"}</h3>
            {error && <div className="success-message">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="description">Vehicle Description</label>
                <input
                  type="text"
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="vehicleplate">Vehicle Plate</label>
                <input
                  type="text"
                  id="vehicleplate"
                  value={formData.vehicleplate}
                  onChange={(e) =>
                    setFormData({ ...formData, vehicleplate: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="agentcode">Agent Code</label>
                <input
                  type="text"
                  id="agentcode"
                  value={formData.agentcode}
                  onChange={(e) =>
                    setFormData({ ...formData, agentcode: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  id="city"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="postcode">Postcode</label>
                <input
                  type="text"
                  id="postcode"
                  value={formData.postcode}
                  onChange={(e) =>
                    setFormData({ ...formData, postcode: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="startlocationlatitude">Start Latitude</label>
                <input
                  type="number"
                  id="startlocationlatitude"
                  value={formData.startlocationlatitude}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      startlocationlatitude: e.target.value,
                    })
                  }
                  step="any"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="startlocationlongitude">Start Longitude</label>
                <input
                  type="number"
                  id="startlocationlongitude"
                  value={formData.startlocationlongitude}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      startlocationlongitude: e.target.value,
                    })
                  }
                  step="any"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="endlocationlatitude">End Latitude</label>
                <input
                  type="number"
                  id="endlocationlatitude"
                  value={formData.endlocationlatitude}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      endlocationlatitude: e.target.value,
                    })
                  }
                  step="any"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="endlocationlongitude">End Longitude</label>
                <input
                  type="number"
                  id="endlocationlongitude"
                  value={formData.endlocationlongitude}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      endlocationlongitude: e.target.value,
                    })
                  }
                  step="any"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="starttime">Start Time</label>
                <input
                  type="time"
                  id="starttime"
                  value={formData.starttime}
                  onChange={(e) =>
                    setFormData({ ...formData, starttime: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="endtime">End Time</label>
                <input
                  type="time"
                  id="endtime"
                  value={formData.endtime}
                  onChange={(e) =>
                    setFormData({ ...formData, endtime: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="height">Height (cm)</label>
                <input
                  type="number"
                  id="height"
                  value={formData.height}
                  onChange={(e) =>
                    setFormData({ ...formData, height: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="length">Length (cm)</label>
                <input
                  type="number"
                  id="length"
                  value={formData.length}
                  onChange={(e) =>
                    setFormData({ ...formData, length: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="width">Width (cm)</label>
                <input
                  type="number"
                  id="width"
                  value={formData.width}
                  onChange={(e) =>
                    setFormData({ ...formData, width: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="vehiclecubage">Cubage (cm³)</label>
                <input
                  type="number"
                  id="vehiclecubage"
                  value={formData.vehiclecubage}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label htmlFor="weight">Weight (kg)</label>
                <input
                  type="number"
                  id="weight"
                  value={formData.weight}
                  onChange={(e) =>
                    setFormData({ ...formData, weight: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="active">Active Status</label>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) =>
                      setFormData({ ...formData, active: e.target.checked })
                    }
                  />
                  <label htmlFor="active" className="toggle-label">
                    <span className="toggle-text">
                      {formData.active ? "Active" : "Inactive"}
                    </span>
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="glass-btn cancel-btn"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="glass-btn submit-btn">
                  Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isDetailsModalOpen && selectedVehicle && (
        <div className="modal-overlay">
          <div className="modal-content details-modal">
            <h3>Vehicle Details</h3>
            <div className="vehicle-detail-row">
              <span className="detail-label">ID:</span>
              <span className="detail-value">{selectedVehicle.id}</span>
            </div>
            <div className="vehicle-detail-row">
              <span className="detail-label">Description:</span>
              <span className="detail-value">
                {selectedVehicle.description}
              </span>
            </div>
            <div className="vehicle-detail-row">
              <span className="detail-label">Vehicle Plate:</span>
              <span className="detail-value">
                {selectedVehicle.vehicleplate}
              </span>
            </div>
            <div className="vehicle-detail-row">
              <span className="detail-label">Agent Code:</span>
              <span className="detail-value">{selectedVehicle.agentcode}</span>
            </div>
            <div className="vehicle-detail-row">
              <span className="detail-label">City:</span>
              <span className="detail-value">{selectedVehicle.city}</span>
            </div>
            <div className="vehicle-detail-row">
              <span className="detail-label">Postcode:</span>
              <span className="detail-value">{selectedVehicle.postcode}</span>
            </div>
            <div className="vehicle-detail-row">
              <span className="detail-label">Start Location:</span>
              <span className="detail-value">
                {selectedVehicle.startlocationlatitude},{" "}
                {selectedVehicle.startlocationlongitude}
              </span>
            </div>
            <div className="vehicle-detail-row">
              <span className="detail-label">End Location:</span>
              <span className="detail-value">
                {selectedVehicle.endlocationlatitude},{" "}
                {selectedVehicle.endlocationlongitude}
              </span>
            </div>
            <div className="vehicle-detail-row">
              <span className="detail-label">Operating Hours:</span>
              <span className="detail-value">
                {selectedVehicle.starttime} - {selectedVehicle.endtime}
              </span>
            </div>
            <div className="vehicle-detail-row">
              <span className="detail-label">Dimensions:</span>
              <span className="detail-value">
                {selectedVehicle.height}×{selectedVehicle.length}×
                {selectedVehicle.width} cm
              </span>
            </div>
            <div className="vehicle-detail-row">
              <span className="detail-label">Cubage:</span>
              <span className="detail-value">
                {selectedVehicle.vehiclecubage} cm³
              </span>
            </div>
            <div className="vehicle-detail-row">
              <span className="detail-label">Weight:</span>
              <span className="detail-value">{selectedVehicle.weight} kg</span>
            </div>
            <div className="vehicle-detail-row">
              <span className="detail-label">Status:</span>
              <span className="detail-value">
                {selectedVehicle.active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="glass-btn cancel-btn"
                onClick={() => setIsDetailsModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles;
